/**
 * Page Content Extraction Hook
 *
 * Intelligently extracts readable content from the current page
 * in the correct reading order for text-to-speech.
 *
 * WCAG 2.1 Success Criterion 1.3.2: Meaningful Sequence
 * Content is presented in a meaningful sequence that preserves meaning.
 */

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

interface PageContent {
  title: string
  description: string
  mainContent: string
  fullText: string
}

// Selectors for content extraction in priority order
const CONTENT_SELECTORS = [
  'main', // Main content area
  '[role="main"]', // ARIA main landmark
  'article', // Article content
  '.content-area', // Common class name
  '#content', // Common ID
]

// Elements to skip (navigation, headers, footers, etc.)
const SKIP_SELECTORS = [
  'nav',
  'header',
  'footer',
  '[role="navigation"]',
  '[role="banner"]',
  '[role="contentinfo"]',
  'aside',
  '.advertisement',
  'script',
  'style',
  'noscript',
]

/**
 * Extract text content from an element, respecting semantic structure
 */
function extractTextFromElement(element: Element): string {
  const texts: string[] = []

  // Get all text nodes and headings in document order
  const walker = document.createTreeWalker(
    element,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        // Skip if inside a skip selector
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element
          if (SKIP_SELECTORS.some((sel) => el.matches(sel) || el.closest(sel))) {
            return NodeFilter.FILTER_REJECT
          }
          // Skip hidden elements
          if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') {
            return NodeFilter.FILTER_REJECT
          }
          const style = window.getComputedStyle(el)
          if (style.display === 'none' || style.visibility === 'hidden') {
            return NodeFilter.FILTER_REJECT
          }
        }
        return NodeFilter.FILTER_ACCEPT
      },
    },
  )

  let node: Node | null
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text && text.length > 0) {
        texts.push(text)
      }
    }
  }

  return texts.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * Get page title with appropriate context
 */
function getPageTitle(): string {
  // Try h1 first (most semantic)
  const h1 = document.querySelector('h1')
  if (h1) {
    return h1.textContent?.trim() || ''
  }

  // Fallback to document title
  return document.title.replace(' - LINA', '').replace(' | LINA', '').trim()
}

/**
 * Get page description/subtitle
 */
function getPageDescription(): string {
  // Look for common description patterns
  const selectors = [
    'h1 + p', // Paragraph after h1
    '[role="main"] > p:first-of-type', // First paragraph in main
    '.description', // Common class
    '[data-description]', // Data attribute
  ]

  for (const selector of selectors) {
    const element = document.querySelector(selector)
    if (element) {
      const text = element.textContent?.trim()
      if (text && text.length > 10 && text.length < 200) {
        return text
      }
    }
  }

  return ''
}

/**
 * Extract main content from page
 */
function getMainContent(): string {
  // Find the main content container
  for (const selector of CONTENT_SELECTORS) {
    const element = document.querySelector(selector)
    if (element) {
      return extractTextFromElement(element)
    }
  }

  // Fallback to body, but exclude nav/header/footer
  const body = document.body.cloneNode(true) as HTMLElement
  SKIP_SELECTORS.forEach((selector) => {
    body.querySelectorAll(selector).forEach((el) => el.remove())
  })

  return extractTextFromElement(body)
}

/**
 * Hook to extract and track page content for TTS
 */
export function usePageContent(): PageContent {
  const location = useLocation()
  const [content, setContent] = useState<PageContent>({
    title: '',
    description: '',
    mainContent: '',
    fullText: '',
  })

  useEffect(() => {
    // Wait for page to fully render
    const timer = setTimeout(() => {
      const title = getPageTitle()
      const description = getPageDescription()
      const mainContent = getMainContent()

      // Construct full text with proper reading order
      const parts: string[] = []

      if (title) {
        parts.push(`Page title: ${title}.`)
      }

      if (description) {
        parts.push(description)
      }

      if (mainContent && mainContent !== description) {
        parts.push(mainContent)
      }

      const fullText = parts.join(' ')

      setContent({
        title,
        description,
        mainContent,
        fullText,
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [location.pathname]) // Re-extract when route changes

  return content
}

/**
 * Hook to extract specific content from a ref
 */
export function useContentFromRef(ref: React.RefObject<HTMLElement>): string {
  const [content, setContent] = useState('')

  useEffect(() => {
    if (!ref.current) return

    const extracted = extractTextFromElement(ref.current)
    setContent(extracted)
  }, [ref])

  return content
}
